import * as React from "react";
import { useState, useEffect } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import OwnerCollectionEditor, { normalizeCollection } from "../OwnerCollectionEditor";
import { OWNERSHIP_SOURCE } from "../../../utils/franchiseAdminModel";

// Edit Default Ownership (Multi-BDM M1) -- the franchise default becomes an editable, ID-backed
// COLLECTION. Held back since A5 precisely so we would not ship a free-text editor we intended to
// replace.
//
// THE CENTRAL UX RULE: a legacy BDMRep value is NEVER pre-populated as though it were already a
// saved assignment. Until a collection is saved, this franchise has NO saved default -- the name
// the fleet currently inherits comes from the legacy source, and the dialog says so plainly.
// Pre-filling it would make un-migrated data look migrated, which is the one impression to avoid.

const sameCollection = (a, b) =>
  a.primaryBdmId === b.primaryBdmId &&
  a.bdmIds.length === b.bdmIds.length &&
  [...a.bdmIds].sort().every((id, i) => id === [...b.bdmIds].sort()[i]);

// The SAVED collection, or an empty one. A franchise resolving via FRANCHISE_LEGACY has no saved
// collection no matter what name it currently displays.
export function savedCollectionOf(franchise) {
  const ownership = franchise.ownership;
  if (!ownership || ownership.source !== OWNERSHIP_SOURCE.FRANCHISE_ASSIGNMENT) {
    return { bdmIds: [], primaryBdmId: null };
  }
  const bdmIds = ownership.owners.map((owner) => owner.bdmId).filter((id) => id != null);
  return normalizeCollection(bdmIds, ownership.primary ? ownership.primary.bdmId : null);
}

export default function EditDefaultOwnershipDialog(props) {
  const open = props.open;
  const onClose = props.onClose;
  const franchise = props.franchise;
  const bdmOptions = props.bdmOptions || [];
  const onSave = props.onSave;
  const onSaved = props.onSaved;

  const ownership = franchise.ownership || {};
  const hasSavedCollection = ownership.source === OWNERSHIP_SOURCE.FRANCHISE_ASSIGNMENT;
  const saved = savedCollectionOf(franchise);

  // What the fleet inherits TODAY when nothing is saved -- the resolver's legacy fallback.
  const legacyInheritedName = hasSavedCollection ? null : ownership.primaryName || null;
  // The legacy value kept for context once a collection IS saved. Reporting still reads it until
  // the warehouse models are repointed, which is why it stays visible rather than disappearing.
  const legacyContextName = hasSavedCollection ? franchise.defaultBdmName || null : null;

  const [value, setValue] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Re-seed from the CURRENT ownership every time the dialog opens, so a reopen never shows stale
  // edits (dialog contract rule 1).
  useEffect(() => {
    if (!open) return;
    setValue(savedCollectionOf(franchise));
    setError(null);
  }, [open, franchise]);

  const hasChanges = !sameCollection(value, saved);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        dim_franchiseID: franchise.dimFranchiseId,
        bdm_ids: value.bdmIds,
      };
      // The API rejects primary_bdm_id alongside an empty collection, so it is omitted rather than
      // sent as null.
      if (value.bdmIds.length) payload.primary_bdm_id = value.primaryBdmId;

      await onSave(payload);
      if (onSaved) onSaved(`${franchise.name} default ownership updated`);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{franchise.name} — Default Ownership</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          {!hasSavedCollection && (
            <Alert severity="info">
              <strong>No franchise default is saved yet.</strong>
              {legacyInheritedName ? (
                <>
                  {" "}Facilities currently inherit <strong>{legacyInheritedName}</strong> from the
                  legacy source, which is not linked to anyone in the BDM directory. Saving owners
                  here creates the first saved default and becomes the authoritative value.
                </>
              ) : (
                <> Saving owners here creates the first saved default for this franchise.</>
              )}
            </Alert>
          )}

          <OwnerCollectionEditor
            bdmOptions={bdmOptions}
            value={value}
            onChange={setValue}
            disabled={saving}
            label="Franchise default BDMs"
            emptyLabel="— No default BDM —"
            emptyStateText="No default BDM. Facilities will inherit nothing unless they set their own."
            helperText="Facilities without their own ownership inherit this collection."
          />

          {legacyContextName && (
            <Typography variant="caption" color="text.secondary">
              Legacy value: <strong>{legacyContextName}</strong>. Reporting still reads this until
              the warehouse models are updated.
            </Typography>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving || !hasChanges} variant="contained">
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
